import { useGetCommissionsQuery, useUpdateCommissionMutation } from "api/TenantSlug/Commission/hooks";
import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible.tsx";
import { DrawerTable, DrawerTableColumn } from "components/UI/DrawerTable/DrawerTable.tsx";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell.tsx";
import { FieldValueType } from "components/UI/Table/UI/InteractiveCell/components/FieldWrapper.tsx";
import { ComposedValue } from "components/UI/Table/UI/InteractiveCell/components/WithPayoutFormula";
import { InteractiveCell } from "components/UI/Table/UI/InteractiveCell/InteractiveCell";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC } from "react";
import { toast } from "react-toastify";
import { updateCommission } from "utils/commission/updateCommission";

import CommissionData = App.Data.CommissionData;

interface ChildCommissionsSectionProps {
    parentCommissionId: string;
    commission: CommissionData;
    onRefetchTable?: () => void;
    onRefetchParent?: () => void;
}

export const ChildCommissionsSection: FC<ChildCommissionsSectionProps> = ({
    parentCommissionId,
    commission,
    onRefetchTable,
    onRefetchParent,
}) => {
    const { t } = usePaldockTranslation("drawers.edit-commission");
    const { t: tGeneral } = usePaldockTranslation("general");

    // Fetch child commissions with proper includes
    const fetchChildCommissionsQuery = useGetCommissionsQuery(`child-commissions-${parentCommissionId}`, {
        filter: {
            filter: {
                parent_id: parentCommissionId,
            },
            include: "offer,user,commissionGroup",
        },
    });

    const { mutateAsync: updateCommissionMutation } = useUpdateCommissionMutation();

    const handleUpdateCommission = (value: ComposedValue, record: CommissionData) => {
        return updateCommission({
            commissionId: record.id,
            value,
            updateCommissionMutation,
            onSuccess: () => {
                fetchChildCommissionsQuery.refetch();
                onRefetchTable?.();
                onRefetchParent?.();
            },
            successMessage: t("messages.update_success"),
            errorMessage: t("messages.update_error"),
        });
    };

    const handleUpdatePayoutPlaceholder = async (value: FieldValueType, record: CommissionData) => {
        const payoutPlaceholder = value !== undefined && value !== null && value !== "" ? String(value) : null;
        await updateCommissionMutation(
            { commissionId: record.id, request: { payout_placeholder: payoutPlaceholder } },
            {
                onSuccess: () => {
                    toast.success(t("messages.update_success"));
                    fetchChildCommissionsQuery.refetch();
                    onRefetchTable?.();
                    onRefetchParent?.();
                },
                onError: () => {
                    toast.error(t("messages.update_error"));
                },
            }
        );
    };

    const children = fetchChildCommissionsQuery.data?.commissions || [];
    const dataSource = [commission, ...children];
    const isWorkspaceRow = (record: CommissionData) => record.id === commission.id;

    const columns: DrawerTableColumn<CommissionData>[] = [
        {
            title: t("child_commissions.group"),
            dataIndex: "commissionGroup",
            key: "group",
            width: "40%",
            render: (_, record) => {
                const label = isWorkspaceRow(record)
                    ? t("child_commissions.workspace_commission")
                    : record.commissionGroup === null
                      ? tGeneral("default_user_group")
                      : (record.commissionGroup?.title ?? record.user?.nickname ?? "-");
                return isWorkspaceRow(record) ? <span className="font-bold">{label}</span> : label;
            },
        },
        {
            title: t("child_commissions.commission"),
            dataIndex: "payout_formula",
            key: "commission",
            width: "30%",
            render: (formula: string, record: CommissionData) =>
                formula ? (
                    <div className={isWorkspaceRow(record) ? "font-bold" : undefined}>
                        <InteractiveCell.PayoutFormula
                            type={"input"}
                            defaultValue={{
                                payoutFormula: formula,
                                formulaType: record.formula_type as "formula" | "percent" | "fix",
                                isEnabled: record.is_enabled,
                            }}
                            currencyId={record.offer?.currency_id}
                            isEnabled={record.is_enabled}
                            readOnly={false}
                            onSave={(value) => handleUpdateCommission(value, record)}
                        />
                    </div>
                ) : (
                    <EmptyCell />
                ),
        },
        {
            title: t("child_commissions.display_amount"),
            dataIndex: "payout_placeholder",
            key: "display_amount",
            width: "30%",
            render: (value: string | null, record: CommissionData) => (
                <div className={isWorkspaceRow(record) ? "font-bold" : undefined}>
                    <InteractiveCell.Input
                        value={value ?? ""}
                        onSave={(newValue) => handleUpdatePayoutPlaceholder(newValue, record)}
                    />
                </div>
            ),
        },
    ];

    return (
        <DrawerCollapsible title={t("child_commissions.title")}>
            <DrawerTable dataSource={dataSource} columns={columns} rowKey={(record) => record.id} />
        </DrawerCollapsible>
    );
};
